// The plan and the top picks as one story (design review 2026-09-26, item C1).
//
// The synthesis prompt writes hostNote as EXACTLY three paragraphs: the read
// and the morning, then midday and the afternoon, then the evening and the days
// ahead. topPicks is the one-line version of the same three windows. Shown
// separately, the page said the morning pick twice (three times with the beach
// notes) and left the afternoon reading "Open, your call." in 7 of 9 briefs
// between 2026-09-18 and 09-26, while paragraph 2 always had an afternoon in it.
// Merged, each daypart carries its pick as a subhead over its own paragraph,
// and a null pick simply leaves the paragraph to speak.
//
// The merge is only safe when the contract holds. brief-validation.js now asks
// for three paragraphs, but archived briefs never regenerate and a retry can
// still fail, so anything else returns null and the page keeps the separate
// picks list and plan.

const WINDOWS = [
  ['morning', 'Morning'],
  ['afternoon', 'Afternoon'],
  ['evening', 'Evening']
];

// Same split the plan has always rendered with, and the same one the
// validator counts with, so the two cannot disagree about a paragraph.
export function planParagraphs(text) {
  if (typeof text !== 'string') return [];
  return text
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
}

// The paragraphs as the dayparts see them. From 2026-09-28 the model has set
// the day's hook on a line of its own ("Light NE breeze and sunny skies set up
// a calm Tuesday across the harbor.") and then written the dayparts, where the
// prompt asks for the hook to open the morning paragraph. On 9/28 that made
// three paragraphs, so the morning's pick sat over the hook and the morning's
// plan was filed under Afternoon; on 9/29 it made four, and the page fell back
// to the separate picks list. A first paragraph with no anchor and a single
// sentence is that hook, and it is read as the start of the morning, which is
// where the prompt puts it. Anything longer is a paragraph and counts as one.
// brief-validation.js counts with this too, so the page and the retry agree.
export function daypartParagraphs(text) {
  const paragraphs = planParagraphs(text);
  if (paragraphs.length > 1 && isHook(paragraphs[0])) {
    return [`${paragraphs[0]} ${paragraphs[1]}`, ...paragraphs.slice(2)];
  }
  return paragraphs;
}

function isHook(p) {
  const sentences = p.split(/[.!?](?=\s|$)/).filter((s) => s.trim()).length;
  return !p.includes('**') && sentences <= 1 && p.length <= 220;
}

// [{ id, label, pick, paragraph }] for the three dayparts, or null when the
// brief does not meet the contract (not three paragraphs, or no windowed
// topPicks, as in briefs from before 2026-06-11).
export function planDayparts(hostNote, topPicks) {
  const paragraphs = daypartParagraphs(hostNote);
  if (paragraphs.length !== 3) return null;
  if (!topPicks || typeof topPicks !== 'object') return null;
  if (!WINDOWS.some(([id]) => topPicks[id])) return null;
  return WINDOWS.map(([id, label], i) => ({
    id,
    label,
    pick: typeof topPicks[id] === 'string' && topPicks[id].trim() ? topPicks[id].trim() : null,
    paragraph: paragraphs[i]
  }));
}
